// src/components/KeyValuesDashboard/components/KeyValueFilters.tsx

import { Building2, Calendar, Database, Key, Users, GitCompare } from 'lucide-react';
import type { AvailableKey } from '@/types/analytics';
import NetworkSelector from '@/components/layout/NetworkSelector';

interface KeyValueFiltersProps {
  startDate: string;
  endDate: string;
  onDateChange: (start: string, end: string) => void;
  selectedDomains: string[];
  onDomainsChange: (domains: string[]) => void;
  selectedKey: string;
  onKeyChange: (key: string) => void;
  availableKeys: AvailableKey[];
  mode: 'all' | 'compare';
  onModeChange: (mode: 'all' | 'compare') => void;
  selectedValues: string[];
  onValuesChange: (values: string[]) => void;
  availableValues: string[];
  isLoadingOptions: boolean;
}

export function KeyValueFilters({
  startDate,
  endDate,
  onDateChange,
  selectedKey,
  onKeyChange,
  availableKeys,
  mode,
  onModeChange,
  selectedValues,
  onValuesChange,
  availableValues,
  isLoadingOptions,
}: KeyValueFiltersProps) {
  
  const handleValueToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onValuesChange(selectedValues.filter(v => v !== value));
    } else {
      onValuesChange([...selectedValues, value]);
    }
  };

  return (
    <div className="p-6 space-y-6">
      
      {/* Primeira linha: Conta GAM e datas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Building2 className="w-4 h-4 text-indigo-500" />
            Conta GAM
          </label>
          <NetworkSelector variant="filtro" />
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Calendar className="w-4 h-4 text-indigo-500" />
            Data Inicial
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => onDateChange(e.target.value, endDate)}
            className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white transition-all"
          />
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Calendar className="w-4 h-4 text-indigo-500" />
            Data Final
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onDateChange(startDate, e.target.value)}
            className="w-full px-4 py-2.5 text-sm border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white transition-all"
          />
        </div>
      </div>

      {/* Segunda linha: Seleção de Key */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Key className="w-4 h-4 text-indigo-500" />
          Selecione a UTM / Custom Key
          <span className="text-rose-500">*</span>
        </label>
        <select
          value={selectedKey}
          onChange={(e) => onKeyChange(e.target.value)}
          className="w-full px-4 py-2.5 text-sm border-2 border-indigo-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white transition-all"
          disabled={isLoadingOptions}
        >
          <option value="">Selecione uma chave...</option>
          {availableKeys.map((key) => (
            <option key={key.custom_key} value={key.custom_key}>
              {key.custom_key} ({key.value_count} valores, ${key.total_revenue.toFixed(2)})
            </option>
          ))}
        </select>
      </div>

      {/* Terceira linha: Modo de Comparação */}
      {selectedKey && (
        <>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <GitCompare className="w-4 h-4 text-indigo-500" />
              Modo de Análise
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Modo: Todos */}
              <button
                onClick={() => onModeChange('all')}
                className={`group relative p-5 rounded-xl border-2 transition-all text-left overflow-hidden ${
                  mode === 'all'
                    ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 shadow-lg'
                    : 'border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md'
                }`}
              >
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg ${
                      mode === 'all' 
                        ? 'bg-gradient-to-br from-indigo-500 to-purple-600' 
                        : 'bg-slate-100 group-hover:bg-indigo-100'
                    } transition-all`}>
                      <Users className={`w-5 h-5 ${
                        mode === 'all' ? 'text-white' : 'text-slate-400 group-hover:text-indigo-500'
                      }`} />
                    </div>
                    <span className={`font-semibold ${
                      mode === 'all' ? 'text-indigo-900' : 'text-slate-700'
                    }`}>
                      Análise Geral
                    </span>
                    {mode === 'all' && (
                      <span className="ml-auto text-xs bg-indigo-600 text-white px-2.5 py-1 rounded-full font-semibold shadow-sm">
                        Ativo
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600">
                    Visualize e compare todos os {availableValues.length} valores desta key
                  </p>
                </div>
              </button>

              {/* Modo: Comparação Específica */}
              <button
                onClick={() => onModeChange('compare')}
                className={`group relative p-5 rounded-xl border-2 transition-all text-left overflow-hidden ${
                  mode === 'compare'
                    ? 'border-violet-500 bg-gradient-to-br from-violet-50 to-purple-50 shadow-lg'
                    : 'border-slate-200 bg-white hover:border-violet-300 hover:shadow-md'
                }`}
              >
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg ${
                      mode === 'compare' 
                        ? 'bg-gradient-to-br from-violet-500 to-purple-600' 
                        : 'bg-slate-100 group-hover:bg-violet-100'
                    } transition-all`}>
                      <GitCompare className={`w-5 h-5 ${
                        mode === 'compare' ? 'text-white' : 'text-slate-400 group-hover:text-violet-500'
                      }`} />
                    </div>
                    <span className={`font-semibold ${
                      mode === 'compare' ? 'text-violet-900' : 'text-slate-700'
                    }`}>
                      Comparação Seletiva
                    </span>
                    {mode === 'compare' && (
                      <span className="ml-auto text-xs bg-violet-600 text-white px-2.5 py-1 rounded-full font-semibold shadow-sm">
                        Ativo
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600">
                    Selecione valores específicos para análise detalhada
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* Seleção de valores (modo compare) */}
          {mode === 'compare' && (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Database className="w-4 h-4 text-violet-500" />
                Selecione os Valores
                <span className="text-rose-500">*</span>
                {selectedValues.length > 0 && (
                  <span className="ml-auto text-xs text-violet-600 bg-violet-100 px-2 py-1 rounded-full">
                    {selectedValues.length} selecionados
                  </span>
                )}
              </label>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-64 overflow-y-auto p-2 bg-slate-50 rounded-xl border border-slate-200">
                {availableValues.map((value) => {
                  const isSelected = selectedValues.includes(value);
                  return (
                    <button
                      key={value}
                      onClick={() => handleValueToggle(value)}
                      className={`px-3 py-2 text-xs font-medium rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-violet-500 bg-gradient-to-br from-violet-100 to-purple-100 text-violet-900 shadow-sm'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-violet-300 hover:bg-violet-50'
                      }`}
                      title={value}
                    >
                      <span className="truncate block">{value}</span>
                    </button>
                  );
                })}
              </div>

              {selectedValues.length > 0 && (
                <button
                  onClick={() => onValuesChange([])}
                  className="text-xs text-slate-600 hover:text-rose-600 underline transition-colors"
                >
                  Limpar seleção
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
